import PropTypes from 'prop-types'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Trash } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { deleteReply } from '../../../reducers/reputationManagementReducer'
import TextAreaField from '../../FormLegacy/FormFieldsV2/TextAreaField'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import api from '../../../api/api'

const CHAR_LIMIT = 350

const Reply = ({ reply, editingRepliesNotAllowed = false, refetchReviews = () => {} }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState(false)
  const [editedReply, setEditedReply] = useState(reply?.reply)
  const [confirmOpened, setConfirmOpened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { displaySnackbar } = useSnackbar()
  const deleteReplyAction = (...args) => dispatch(deleteReply(...args))

  const onDeleteReply = async () => {
    try {
      setIsLoading(true)
      await deleteReplyAction(reply.reviewId, reply.id)
      setConfirmOpened(false)
      displaySnackbar(t('organisation:reputationManagement.reply.toasts.deleted'))
    } catch (error) {
      console.error(error)
      displaySnackbar(t('organisation:reputationManagement.reply.errors.deleteFailed'), true)
    } finally {
      setIsLoading(false)
    }
  }

  const editReply = async () => {
    try {
      setIsLoading(true)
      await api.postReputationReviewReply(reply.reviewId, editedReply)
      setIsEditing(false)
      refetchReviews()
      displaySnackbar(t('organisation:reputationManagement.reply.toasts.updated'), false)
    } catch (error) {
      displaySnackbar(t('organisation:reputationManagement.reply.errors.updateFailed'), true)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="review__reply-wrap">
        {isEditing ? (
          <>
            <TextAreaField
              name="reply"
              onChange={({ target: { value } }) => {
                if (value.length > CHAR_LIMIT) {
                  setError(true)
                  return
                }

                setEditedReply(value)
                error && setError(false)
              }}
              value={editedReply}
              placeholder={t('organisation:reputationManagement.review.replyPlaceholder')}
              additionalClass="review__reply-field"
              isError={error}
            />
            <div className="review__buttons-wrap">
              <button className="button button-transparent" onClick={() => setIsEditing(false)}>
                {t('common:actions.cancel')}
              </button>
              <button
                className="button button-primary"
                onClick={editReply}
                disabled={error || !editedReply?.trim()}
              >
                {t('organisation:reputationManagement.reply.updateReply')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="review__reply">{reply?.reply}</div>
            {!editingRepliesNotAllowed && (
              <div className="review__buttons-wrap">
                <button
                  className="button button-primary"
                  disabled={isLoading}
                  onClick={() => setIsEditing(true)}
                >
                  {t('common:actions.edit')}
                </button>
                <button
                  className={`button button-gray ${isLoading ? 'is-loading' : ''}`}
                  onClick={() => setConfirmOpened(true)}
                  disabled={isLoading}
                >
                  <Trash />
                  {t('common:actions.delete')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <ConfirmPopup
        title={t('organisation:reputationManagement.reply.confirmation.title')}
        body={t('organisation:reputationManagement.reply.confirmation.body')}
        cancelText={t('common:actions.cancel')}
        confirmText={t('organisation:reputationManagement.reply.confirmation.confirmText')}
        isOpen={confirmOpened}
        cancelCallback={() => setConfirmOpened(false)}
        confirmCallback={onDeleteReply}
        isLoading={isLoading}
      />
    </>
  )
}

Reply.propTypes = {
  editingRepliesNotAllowed: PropTypes.bool,
  reply: PropTypes.object,
  refetchReviews: PropTypes.func,
}

export default Reply
